import { useState } from 'react';
import { Alert, Button, Center, Divider, Paper, PasswordInput, SegmentedControl, Select, SimpleGrid, Stack, Text, TextInput, Title } from '@mantine/core';
import { api, ErrorApi } from '../api';
import { ActivarDobleFactor } from '../componentes/DobleFactor';
import { useSesion } from '../sesion';
import { avisarError, avisarOk } from '../comun';

function Marco({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <Center mih="100vh" p="md">
      <Paper withBorder shadow="sm" p="xl" w="100%" maw={520}>
        <Stack>
          <Center>
            <img src="/icono.svg" alt="" width={56} height={56} />
          </Center>
          <Title order={2} ta="center">
            {titulo}
          </Title>
          {children}
        </Stack>
      </Paper>
    </Center>
  );
}

export function PaginaAcceso() {
  const s = useSesion();
  const [email, setEmail] = useState('');
  const [clave, setClave] = useState('');
  const [ticket, setTicket] = useState<string | null>(null);
  const [codigo, setCodigo] = useState('');
  const [conRecuperacion, setConRecuperacion] = useState(false);
  const [enviando, setEnviando] = useState(false);

  const entrar = async (e: React.FormEvent) => {
    e.preventDefault();
    setEnviando(true);
    try {
      const r = await api.post<{ requiere2fa?: boolean; ticket?: string }>('/auth/acceso', { email, clave });
      if (r.requiere2fa && r.ticket) {
        setTicket(r.ticket);
        setClave('');
      } else {
        await s.recargar();
      }
    } catch (err) {
      avisarError(err);
    } finally {
      setEnviando(false);
    }
  };

  const verificar = async (e: React.FormEvent) => {
    e.preventDefault();
    setEnviando(true);
    try {
      await api.post('/auth/2fa/verificar', { ticket, codigo });
      await s.recargar();
    } catch (err) {
      // Si el ticket caducó (5 min), se vuelve a pedir la contraseña.
      if (err instanceof ErrorApi && err.codigo === 'TICKET_CADUCADO') {
        setTicket(null);
        setCodigo('');
      }
      avisarError(err);
    } finally {
      setEnviando(false);
    }
  };

  if (ticket) {
    return (
      <Marco titulo="Verificación en dos pasos">
        <form onSubmit={verificar}>
          <Stack>
            <Text size="sm" c="dimmed" ta="center">
              {conRecuperacion
                ? 'Escribe uno de tus códigos de recuperación (cada uno vale una vez).'
                : 'Abre tu app de verificación (Google Authenticator, Contraseñas de Apple…) y escribe el código de 6 cifras.'}
            </Text>
            <TextInput
              label={conRecuperacion ? 'Código de recuperación' : 'Código de 6 cifras'}
              placeholder={conRecuperacion ? 'XXXXX-XXXXX' : '123 456'}
              inputMode={conRecuperacion ? 'text' : 'numeric'}
              autoComplete="one-time-code"
              autoFocus
              value={codigo}
              onChange={(e) => setCodigo(e.currentTarget.value)}
              required
            />
            <Button type="submit" loading={enviando} disabled={codigo.trim().length < 6}>
              Verificar
            </Button>
            <Button variant="subtle" size="xs" onClick={() => { setConRecuperacion(!conRecuperacion); setCodigo(''); }}>
              {conRecuperacion ? 'Usar el código de la app' : 'He perdido el móvil: usar un código de recuperación'}
            </Button>
            <Button variant="subtle" size="xs" color="gray" onClick={() => { setTicket(null); setCodigo(''); }}>
              Volver
            </Button>
          </Stack>
        </form>
      </Marco>
    );
  }
  return (
    <Marco titulo="Acceder">
      <form onSubmit={entrar}>
        <Stack>
          <TextInput label="Email" type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.currentTarget.value)} required />
          <PasswordInput label="Contraseña" autoComplete="current-password" value={clave} onChange={(e) => setClave(e.currentTarget.value)} required />
          <Button type="submit" loading={enviando}>
            Entrar
          </Button>
          <Text size="xs" c="dimmed" ta="center">
            ¿Has olvidado la contraseña o has perdido el móvil de verificación? Pídele a la otra persona del negocio que restablezca tu acceso desde Ajustes › Personas.
          </Text>
        </Stack>
      </form>
    </Marco>
  );
}

/** Pantalla que obliga a activar la verificación en dos pasos (si el servidor lo exige). */
export function PaginaConfigurar2fa() {
  const s = useSesion();
  return (
    <Marco titulo="Activa la verificación en dos pasos">
      <Alert color="blue">Este servidor exige verificación en dos pasos para proteger los datos fiscales. Solo se hace una vez.</Alert>
      <ActivarDobleFactor onTerminado={() => void s.recargar()} />
      <Button variant="subtle" size="xs" onClick={() => void s.salir()}>
        Salir
      </Button>
    </Marco>
  );
}

export function PaginaCambiarClave({ obligatorio = false }: { obligatorio?: boolean }) {
  const s = useSesion();
  const [actual, setActual] = useState('');
  const [nueva, setNueva] = useState('');
  const [repetida, setRepetida] = useState('');
  const [enviando, setEnviando] = useState(false);
  const guardar = async (e: React.FormEvent) => {
    e.preventDefault();
    setEnviando(true);
    try {
      await api.post('/auth/clave', { actual, nueva });
      avisarOk('Contraseña cambiada');
      setActual('');
      setNueva('');
      setRepetida('');
      await s.recargar();
    } catch (err) {
      avisarError(err);
    } finally {
      setEnviando(false);
    }
  };
  const formulario = (
    <form onSubmit={guardar}>
      <Stack>
        {obligatorio && <Alert color="yellow">Es tu primer acceso: elige una contraseña propia para continuar.</Alert>}
        <PasswordInput label="Contraseña actual" autoComplete="current-password" value={actual} onChange={(e) => setActual(e.currentTarget.value)} required />
        <PasswordInput label="Nueva contraseña" description="Mínimo 10 caracteres" autoComplete="new-password" value={nueva} onChange={(e) => setNueva(e.currentTarget.value)} required />
        <PasswordInput
          label="Repite la nueva contraseña"
          autoComplete="new-password"
          value={repetida}
          onChange={(e) => setRepetida(e.currentTarget.value)}
          error={repetida && repetida !== nueva ? 'No coincide' : undefined}
          required
        />
        <Button type="submit" loading={enviando} disabled={nueva.length < 10 || nueva !== repetida}>
          Cambiar contraseña
        </Button>
        {obligatorio && (
          <Button variant="subtle" onClick={() => void s.salir()}>
            Salir
          </Button>
        )}
      </Stack>
    </form>
  );
  return obligatorio ? <Marco titulo="Cambia tu contraseña">{formulario}</Marco> : formulario;
}

/** Primer arranque: crea la cuenta inicial, el negocio y su perfil fiscal. */
export function PaginaConfiguracionInicial() {
  const s = useSesion();
  const [codigo, setCodigo] = useState('');
  const [d, setD] = useState({
    nombre: '',
    email: '',
    clave: '',
    rol: 'gestor',
    negocio: '',
    nif: '',
    nombreFiscal: '',
    actividadCodigo: 'A',
    actividadTipo: '05',
    epigrafe: '861',
    regimenIrpf: 'EDS',
    regimenIgic: 'REPEP',
  });
  const [enviando, setEnviando] = useState(false);
  const campo = (k: keyof typeof d) => ({ value: d[k], onChange: (e: React.ChangeEvent<HTMLInputElement>) => setD({ ...d, [k]: e.currentTarget.value }) });

  const crear = async (e: React.FormEvent) => {
    e.preventDefault();
    setEnviando(true);
    try {
      await api.post('/auth/configuracion-inicial', {
        codigo: codigo || undefined,
        nombre: d.nombre,
        email: d.email,
        clave: d.clave,
        rol: d.rol,
        negocio: { nombre: d.negocio, nif: d.nif || null, nombreFiscal: d.nombreFiscal || null },
        perfil: {
          actividadCodigo: d.actividadCodigo,
          actividadTipo: d.actividadTipo,
          epigrafe: d.epigrafe,
          regimenIrpf: d.regimenIrpf,
          regimenIgic: d.regimenIgic,
        },
      });
      await s.recargar();
      window.location.reload();
    } catch (err) {
      avisarError(err);
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Marco titulo="Configuración inicial">
      <Text size="sm" c="dimmed" ta="center">
        Crea la primera cuenta y el negocio. Después podrás dar acceso a otras personas desde Ajustes.
      </Text>
      <form onSubmit={crear}>
        <Stack>
          {s.requiereCodigo && (
            <PasswordInput
              label="Código de configuración"
              description="Es el valor de CODIGO_CONFIGURACION en el archivo .env del servidor (no en el de tu equipo). Solo sirve para crear la primera cuenta."
              value={codigo}
              onChange={(e) => setCodigo(e.currentTarget.value)}
              required
            />
          )}
          <Divider label="Tu cuenta" labelPosition="left" />
          <TextInput label="Tu nombre" required {...campo('nombre')} />
          <TextInput label="Email" type="email" required {...campo('email')} />
          <PasswordInput label="Contraseña" description="Mínimo 10 caracteres" required {...campo('clave')} />
          <div>
            <Text size="sm" fw={500} mb={4}>
              Tu papel en el negocio
            </Text>
            <SegmentedControl
              fullWidth
              value={d.rol}
              onChange={(v) => setD({ ...d, rol: v })}
              data={[
                { value: 'titular', label: 'Soy la persona titular' },
                { value: 'gestor', label: 'Ayudo a gestionarlo' },
              ]}
            />
          </div>
          <Divider label="El negocio" labelPosition="left" />
          <TextInput label="Nombre del negocio" placeholder="Nombre comercial" required {...campo('negocio')} />
          <SimpleGrid cols={{ base: 1, xs: 2 }}>
            <TextInput label="NIF del titular" description="Opcional ahora" {...campo('nif')} />
            <TextInput label="Nombre fiscal" description="APELLIDOS, NOMBRE" {...campo('nombreFiscal')} />
          </SimpleGrid>
          <Divider label="Situación fiscal" labelPosition="left" />
          <SimpleGrid cols={{ base: 1, xs: 3 }}>
            <Select
              label="Actividad"
              data={[
                { value: 'A', label: 'A · Incluida en el IAE' },
                { value: 'B', label: 'B · No incluida en el IAE' },
              ]}
              value={d.actividadCodigo}
              onChange={(v) => setD({ ...d, actividadCodigo: v ?? 'A' })}
            />
            <Select
              label="Tipo"
              data={[
                { value: '03', label: '03 · Empresarial' },
                { value: '04', label: '04 · Profesional artística' },
                { value: '05', label: '05 · Resto profesionales' },
              ]}
              value={d.actividadTipo}
              onChange={(v) => setD({ ...d, actividadTipo: v ?? '05' })}
            />
            <TextInput label="Epígrafe IAE" description="Sin punto" {...campo('epigrafe')} />
          </SimpleGrid>
          <SimpleGrid cols={{ base: 1, xs: 2 }}>
            <Select
              label="IRPF"
              data={[
                { value: 'EDS', label: 'Estimación directa simplificada' },
                { value: 'EDN', label: 'Estimación directa normal' },
              ]}
              value={d.regimenIrpf}
              onChange={(v) => setD({ ...d, regimenIrpf: v ?? 'EDS' })}
            />
            <Select
              label="IGIC"
              data={[
                { value: 'REPEP', label: 'Pequeño empresario (REPEP)' },
                { value: 'GENERAL', label: 'Régimen general' },
              ]}
              value={d.regimenIgic}
              onChange={(v) => setD({ ...d, regimenIgic: v ?? 'REPEP' })}
            />
          </SimpleGrid>
          <Button type="submit" loading={enviando}>
            Crear y entrar
          </Button>
        </Stack>
      </form>
    </Marco>
  );
}
